import { Checkbox } from '@sunwebgroup/uds-ui/checkbox'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import {
  type BusFilterType,
  BusFilterValues,
} from '@/scopes/account/group-responsible/domain/types'
import { useGroupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/useGroupResponsibleStore'
import {
  getFilteredParticipantsBusBooked,
  getParticipantsBusBookedCounts,
} from '../selectors'
import styles from './BusTable.module.css'
import { EditBusSheetPortal } from './EditBusSheetPortal'
import { FilterToggleGroup } from './FilterToggleGroup'
import { ParticipantExtraInfoSheetPortal } from './ParticipantExtraInfoSheetPortal'
import { RoomAssignmentStatusBanner } from './RoomAssignmentStatusBanner'
import {
  ChevronCell,
  EmptyTableMessage,
  Subtitle,
  Table,
  TableCell,
  TableCellGrow,
  TableCellSelectable,
  TableHead,
  TableHeadGrow,
  TableHeadSelectable,
  TableRow,
} from './Table'
import { TableSearch } from './TableSearch'
import { Toolbar } from './Toolbar'

const hideColumnsBreakpoint = 'primitives_breakpoint_m_max'
const showMobileLayoutBreakpoint = 'primitives_breakpoint_m_min'

export function BusTable() {
  const [filter, setFilter] = React.useState<BusFilterType>('all')
  const [searchTerm, setSearchTerm] = React.useState('')
  const counts = useGroupResponsibleStore(getParticipantsBusBookedCounts)
  const filteredParticipantsAndBusInfo = useGroupResponsibleStore(state =>
    getFilteredParticipantsBusBooked(state, filter, searchTerm),
  )
  const selectedParticipantIds = useGroupResponsibleStore(state => state.selectedParticipantIds)
  const toggleParticipantSelection = useGroupResponsibleStore(state => state.toggleParticipantSelection)
  const changeExtraInfoId = useGroupResponsibleStore(state => state.changeExtraInfoId)
  const intl = useIntl()
  type CountKeys = keyof ReturnType<typeof getParticipantsBusBookedCounts>
  const filters: { value: BusFilterType, label: string, countKey: CountKeys }[] = [
    { value: BusFilterValues.ALL, label: 'groups_responsible_all', countKey: 'all' },
    { value: BusFilterValues.BOOKED, label: 'groups_responsible_status_booked', countKey: 'booked' },
    { value: BusFilterValues.NOT_BOOKED, label: 'groups_responsible_status_not_booked', countKey: 'notBooked' },
  ]

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value)
  }

  function handleFilterChange(newFilter: BusFilterType) {
    setFilter(newFilter)
  }

  const filteredParticipantIds = filteredParticipantsAndBusInfo
    .map(p => p.id)
  const isAllFilteredSelected = filteredParticipantIds.length > 0
    && filteredParticipantIds.every(id => selectedParticipantIds.has(id))

  function handleSelectAll() {
    if (isAllFilteredSelected) {
      filteredParticipantIds.forEach((id) => {
        if (selectedParticipantIds.has(id)) {
          toggleParticipantSelection(id)
        }
      })
    }
    else {
      filteredParticipantIds.forEach((id) => {
        if (!selectedParticipantIds.has(id)) {
          toggleParticipantSelection(id)
        }
      })
    }
  }

  return (
    <>
      <TableSearch searchTerm={searchTerm} onSearchChange={handleSearchChange} />
      <FilterToggleGroup filter={filter} filters={filters} counts={counts} onFilterChange={handleFilterChange} />
      <RoomAssignmentStatusBanner />
      {filteredParticipantsAndBusInfo.length === 0
        ? (
            <EmptyTableMessage />
          )
        : (
            <>
              <Table>
                <thead>
                  <TableRow>
                    <TableHeadSelectable>
                      <label htmlFor="checkbox-all">
                        <Checkbox
                          id="checkbox-all"
                          checked={isAllFilteredSelected}
                          onCheckedChange={handleSelectAll}
                        />
                      </label>
                    </TableHeadSelectable>
                    <TableHeadGrow>
                      <Typography variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_table_name_label' })}
                      </Typography>
                    </TableHeadGrow>
                    <TableHead $size="lg" $hideOn={showMobileLayoutBreakpoint}>
                      <Typography variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_table_bus_stop_label' })}
                      </Typography>
                    </TableHead>
                    <TableHead $hideOn={hideColumnsBreakpoint} $alignRight>
                      <Typography variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_table_bus_number_label' })}
                      </Typography>
                    </TableHead>
                    <TableHead $hideOn={showMobileLayoutBreakpoint} />
                  </TableRow>
                </thead>
                <tbody>
                  {filteredParticipantsAndBusInfo.map((participant) => {
                    const isSelected = selectedParticipantIds.has(participant.id)
                    const isBusProcessing = participant.isBusAssignmentProcessing

                    return (
                      <TableRow
                        key={participant.id}
                        $isSelected={isSelected}
                        onClick={() => changeExtraInfoId(participant.id)}
                      >
                        <TableCellSelectable
                          onClick={(e) => {
                            e.stopPropagation()
                          }}
                        >
                          <label htmlFor={participant.id}>
                            <Checkbox
                              id={participant.id}
                              checked={isSelected}
                              onCheckedChange={() => {
                                toggleParticipantSelection(participant.id)
                              }}
                            />
                          </label>
                        </TableCellSelectable>
                        {!isBusProcessing
                          ? (
                              <TableCellGrow>
                                <Typography variant="body-m-bold-tight">{participant.name}</Typography>
                                {participant.email && (
                                  <Subtitle>{participant.email}</Subtitle>
                                )}
                              </TableCellGrow>
                            )
                          : (
                              <TableCellGrow className={styles.cellGrowLoading}>
                                <div className={styles.cellWithSubtitleLoading}>
                                  <Typography variant="body-m-bold-tight">{participant.name}</Typography>
                                  {participant.email && (
                                    <Subtitle>{participant.email}</Subtitle>
                                  )}
                                </div>
                                <Typography variant="body-m-regular-tight" className={styles.processingMobile}>
                                  {intl.formatMessage({ id: 'groups_responsible_processing' })}
                                </Typography>
                              </TableCellGrow>
                            )}
                        <TableCell $size="lg" $hideOn={showMobileLayoutBreakpoint} $isMuted={isBusProcessing}>
                          {isBusProcessing
                            ? intl.formatMessage({ id: 'groups_responsible_bus_changes_processing' })
                            : (participant.busInfo?.location
                              ?? (participant.isRegistered
                                ? intl.formatMessage({ id: 'groups_responsible_table_self_drive_label' })
                                : '-'))}
                        </TableCell>
                        <TableCell $hideOn={hideColumnsBreakpoint} $alignRight $isMuted={isBusProcessing}>
                          {isBusProcessing
                            ? intl.formatMessage({ id: 'groups_responsible_processing' })
                            : (participant.busInfo?.number ? participant.busInfo?.number : '-')}
                        </TableCell>
                        <ChevronCell $hideOn={showMobileLayoutBreakpoint} />
                      </TableRow>
                    )
                  })}
                </tbody>
              </Table>
              <ParticipantExtraInfoSheetPortal />
              <Toolbar />
              <EditBusSheetPortal />
            </>
          )}
    </>
  )
}
